// dateFnsUtils.ts
import { addDays, addMinutes, addMonths,addYears, differenceInDays, differenceInMilliseconds, differenceInMonths, differenceInYears, format, formatDistanceToNow, getMonth, isSameDay, subDays, subMonths, subYears, subHours, subMinutes, parse, isValid, parseISO, addHours, parseJSON, set } from 'date-fns';
import { formatInTimeZone, format as formatDate, toZonedTime } from 'date-fns-tz';
import { 
  startOfDay as startOfDayFns,
  endOfDay as endOfDayFns,
  startOfMonth as startOfMonthFns,
  endOfMonth as endOfMonthFns,
  startOfTomorrow as startOfTomorrowFns
} from 'date-fns';

const DATE_FORMAT = 'yyyy-MM-dd';
const DATE_TIME_FORMAT = 'yyyy-MM-dd HH:mm';
const UTC_TIMESTAMP = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

export function startOfTomorrow(){
 return startOfTomorrowFns()
}

/**
 * Returns a relative time string (e.g., "5 minutes ago") from a UTC date
 * @param dateString ISO date string or null/undefined
 * @returns Formatted relative time string or null if no date provided
 */
export function fromNowUTC(dateString?: string | null): string | null {
  if (!dateString) return null;
  
  try {
    const parsedDate = parseISO(dateString);
    const utcDate = toZonedTime(parsedDate, 'UTC');
    return formatDistanceToNow(utcDate, { addSuffix: true });
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
}

function ensureUtcString(value: string): string {
  // If value already includes Z or a timezone offset, don't touch it
  if (typeof value !== 'string') return value;
  if (/Z$|[+-]\d{2}:\d{2}$/.test(value)) return value;
  return value + 'Z';
}

/**
 * Determines the appropriate format and formats the date based on timezone
 * @param value Date string to format
 * @param columnName Column name to check for TIME inclusion
 * @param timezone Timezone to use (UTC or other)
 * @returns Formatted date string
 */
export function formatDateByColumnAndTimezone(
  value: string, 
  columnName: string, 
  timezone: string
): string {
  if (!value) return '';
  
  // Determine the format to use
  const isValidInAnyFormat = isValidDateFormat(value, [
    DATE_FORMAT, 
    `${DATE_TIME_FORMAT}:ss`, 
    UTC_TIMESTAMP
  ]);
  
  const formatToUse = (isValidInAnyFormat || columnName.toUpperCase().includes("TIME"))
    ? DATE_TIME_FORMAT
    : DATE_FORMAT;
  
  try {
    // Parse the date
    let dateObj: Date;
    try {
      // normalize the input string to make sure parseISO treats it as UTC
      const valueWithUtc = ensureUtcString(value);
      // Parse the ISO string, now guaranteed to be interpreted as UTC
      dateObj = parseISO(valueWithUtc);
      if (!isValid(dateObj)) {
        throw new Error('Invalid ISO date');
      }
    } catch {
      // If ISO parsing fails, try with the determined format
      dateObj = parse(value, formatToUse, new Date());
    }
    
    if (!isValid(dateObj)) {
      return value; // Return original value if parsing fails
    }
    
    // Format based on timezone
    if (timezone === "UTC") {
      const utcDate = toZonedTime(dateObj, 'UTC');
      return formatDate(utcDate, formatToUse);
    } else {
      return toJSTDateTime(dateObj, formatToUse);
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return value; // Return original value on error
  }
}

/**
 * Checks if a date string is valid according to any of the provided formats
 * @param value Date string to validate
 * @param supportedFormats Array of date format strings
 * @returns Boolean indicating if the date is valid in any of the formats
 */
export function isValidDateFormat(value: string, supportedFormats: string[]): boolean {
  // Add more robust type checking
  if (!value || typeof value !== 'string') return false;
  
  // Also check if it's an empty string or just whitespace
  if (value.trim() === '') return false;

  for (const format of supportedFormats) {
    try {
      const date = parse(value, format, new Date());
      if (isValid(date)) {
        return true;
      }
    } catch (error) {
      // Continue to next format if parsing fails
      continue;
    }
  }
  return false;
}


/**
 * Converts a date to JST timezone and formats it
 * @param date Date object or string to convert
 * @param formatString Format string to use
 * @returns Formatted date string in JST timezone
 */
export function toJSTDateTime(date: Date | string, formatString: string): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const jstDate = toZonedTime(dateObj, 'Asia/Tokyo');
  return formatDate(jstDate, formatString, { timeZone: 'Asia/Tokyo' });
}

/**
 * Formats a date string according to the specified format and timezone
 * @param dateString ISO date string to format
 * @param formatString Format string to use
 * @param timezone Timezone to use (UTC or JST)
 * @returns Formatted date string
 */
export function formatDateWithTimezone(
  dateString: string,
  formatString: string,
  timezone: string
): string {
  if (!dateString) return '';
  
  try {
    const date = parseISO(dateString);
    
    if (!isValid(date)) {
      return dateString; // Return original if parsing fails
    }
    
    if (timezone === "JST") {
      return toJSTDateTime(date, formatString);
    } else {
      // Assume UTC for any other timezone
      const utcDate = toZonedTime(date, 'UTC');
      return formatDate(utcDate, formatString);
    }
  } catch (error) {
    console.error('Error formatting date with timezone:', error);
    return dateString; // Return original on error
  }
}

/**
 * Checks if a date string or object is valid
 * @param date Date string or object to validate
 * @returns Boolean indicating if the date is valid
 */
export function isValidDate(date: string | Date): boolean {
  if (!date) return false;
  
  try {
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return isValid(dateObj);
  } catch {
    return false;
  }
}

/**
 * Adds or subtracts hours based on timezone
 * @param date Date string or object
 * @param timezone Timezone string (JST or other)
 * @returns Date object with adjusted hours
 */
export function addHoursWithTimezone(date: string | Date, timezone: string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  // Add 9 hours for JST, subtract 9 hours for other timezones (assuming UTC)
  return timezone === "JST" 
    ? addHours(dateObj, 9) 
    : addHours(dateObj, -9);
}

/**
 * Gets the current date adjusted for a specific timezone
 * @param timezone The timezone to adjust for (e.g., 'America/New_York', 'UTC', 'JST')
 * @returns Date object adjusted for the specified timezone
 */
export function getCurrentDateWithTimezone(timezone?: string): Date {
  const now = new Date();
  
  if (!timezone) {
    return now;
  }
  
  // Map JST to proper timezone identifier
  const timezoneId = timezone === 'JST' ? 'Asia/Tokyo' : timezone;

  try {
    return toZonedTime(now, timezoneId);
  } catch (error) {
    console.error(`Error converting to timezone ${timezoneId}:`, error);
    return now;
  }
}


/**
 * Adds time to a date and optionally formats it
 * @param date Date to add time to
 * @param amount Amount of time to add
 * @param unit Unit of time ('minutes', 'hours', 'days', etc.)
 * @param formatString Optional format string
 * @returns Date with added time, formatted if format string provided
 */
export function addTimeToDate(
  date: Date | string,
  amount: number,
  unit: 'minutes' | 'hours' | 'days' | 'months' | 'years',
  formatString?: string
): Date | string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  let resultDate: Date;
  
  switch (unit) {
    case "minutes":
      resultDate = addMinutes(dateObj, amount);
      break;
    case "hours":
      resultDate = addHours(dateObj, amount);
      break;
    case "days":
      resultDate = addDays(dateObj, amount);
      break;
    case "months":
      return addMonths(dateObj, amount);
    case "years":
      return addYears(dateObj, amount);
    default:
      resultDate = dateObj;
  }
  
  // If format string is provided, return formatted string, otherwise return Date object
  return formatString ? formatDate(resultDate, formatString) : resultDate;
}

/**
 * Formats a date string or Date object according to the specified format
 * @param date Date string or Date object to format
 * @param formatString Format string to use
 * @returns Formatted date string
 */
export function formatDateUtil(date: string | Date, formatString: string): string {
  if (!date) return '';
  
  try {
    // Convert to Date object if it's a string
    const dateObj = typeof date === 'string' ? parseISO(date) : date;
    return format(dateObj, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    // Return original value if it's a string, or empty string if it's a Date
    return typeof date === 'string' ? date : '';
  }
}

/**
 * Adds the specified number of days to a date
 * @param date Date to add days to
 * @param days Number of days to add
 * @returns Date with added days
 */
export function addDaysUtil(date: Date, days: number): Date {
  return addDays(date, days);
}

/**
 * Sets the hours and minutes of a date
 * @param date Date to modify
 * @param hours Hours to set
 * @param minutes Minutes to set
 * @returns Date with set hours and minutes
 */
export function setHoursAndMinutes(date: Date | string, hours: number, minutes: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return set(dateObj, {
    hours,
    minutes
  });
}

/**
 * Adds the specified number of minutes to a date
 * @param date Date to add minutes to
 * @param minutes Number of minutes to add
 * @returns Date with added minutes
 */
export function addMinutesUtil(date: Date | string, minutes: number): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return addMinutes(dateObj, minutes);
}

/**
 * Checks if two dates are the same day
 * @param dateLeft First date to compare
 * @param dateRight Second date to compare
 * @returns Boolean indicating if the dates are the same day
 */
export function isSameDayUtil(dateLeft: Date | string, dateRight: Date | string): boolean {
  const dateLeftObj = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
  const dateRightObj = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
  
  return isSameDay(dateLeftObj, dateRightObj);
}

/**
 * Sets date components (year, month, date) while preserving time
 * @param date Date to modify
 * @param components Object with date components to set
 * @returns New date with updated components
 */
export function setDateComponents(
  date: Date | string, 
  components: { year?: number; month?: number; date?: number }
): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  return set(dateObj, components);
}

/**
 * Gets a specific component from a date
 * @param date Date to get component from
 * @param unit Unit to get ('hours', 'minutes', 'date', 'month', 'year')
 * @returns Value of the specified component
 */
export function getDateComponent(date: Date, unit: string): number {
  switch (unit) {
    case 'hours':
      return date.getHours();
    case 'minutes':
      return date.getMinutes();
    case 'seconds':
      return date.getSeconds();
    case 'date':
      return date.getDate();
    case 'month':
      return date.getMonth();
    case 'year':
      return date.getFullYear();
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
}

/**
 * Sets a specific component of a date
 * @param date Date to modify
 * @param unit Unit to set ('hours', 'minutes', 'date', 'month', 'year')
 * @param value Value to set
 * @returns New date with the updated component
 */
export function setDateComponent(date: Date, unit: string, value: number): Date {
  const newDate = new Date(date);
  
  switch (unit) {
    case 'hours':
      newDate.setHours(value);
      break;
    case 'minutes':
      newDate.setMinutes(value);
      break;
    case 'seconds':
      newDate.setSeconds(value);
      break;
    case 'date':
      newDate.setDate(value);
      break;
    case 'month':
      newDate.setMonth(value);
      break;
    case 'year':
      newDate.setFullYear(value);
      break;
    default:
      throw new Error(`Unsupported unit: ${unit}`);
  }
  
  return newDate;
}

/**
 * Gets the current date
 * @returns Current date
 */
export function getCurrentDate(): Date {
  return new Date();
}

/**
 * Subtracts a specified amount of time from a date
 * @param date Date to subtract from
 * @param amount Amount to subtract
 * @param unit Unit of time (days, months, years)
 * @returns New date with the subtracted time
 */

export function subtractFromDate(date: Date | string, amount: number, unit: string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;

  switch (unit) {
    case 'days':
      return subDays(dateObj, amount);
    case 'months':
      return subMonths(dateObj, amount);
    case 'years':
      return subYears(dateObj, amount);
    case 'hours':
      return subHours(dateObj, amount);
    case 'minutes':
      return subMinutes(dateObj, amount);
    default:
      return dateObj;
  }
}

/**
 * Gets the start of the day for a date
 * @param date Date to get start of day for
 * @returns Date set to the start of the day
 */
export function startOfDay(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return startOfDayFns(dateObj);
}

/**
 * Gets the end of the day for a date
 * @param date Date to get end of day for
 * @returns Date set to the end of the day
 */
export function endOfDay(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return endOfDayFns(dateObj);
}

/**
 * Gets the start of the month for a date
 * @param date Date to get start of month for
 * @returns Date set to the start of the month
 */
export function startOfMonth(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return startOfMonthFns(dateObj);
}

/**
 * Gets the end of the month for a date
 * @param date Date to get end of month for
 * @returns Date set to the end of the month
 */
export function endOfMonth(date: Date | string): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return endOfMonthFns(dateObj);
}

/**
 * Calculates the difference between two dates in the specified unit
 * @param dateLeft First date
 * @param dateRight Second date
 * @param unit Unit to calculate difference in (days, months, years)
 * @returns Difference between dates in the specified unit
 */
export function calculateDifference(dateLeft: Date | string, dateRight: Date | string, unit: string): number {
  const dateLeftObj = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
  const dateRightObj = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
  
  switch (unit) {
    case 'days':
      return differenceInDays(dateLeftObj, dateRightObj);
    case 'months':
      return differenceInMonths(dateLeftObj, dateRightObj);
    case 'years':
      return differenceInYears(dateLeftObj, dateRightObj);
    default:
      return 0;
  }
}

/**
 * Parses a date string into a Date object
 * @param dateStr Date string to parse
 * @param format Optional format string for parsing (if not ISO format)
 * @returns Parsed Date object
 */
export function parseDate(dateStr: string | Date, format?: string): Date {
  // If it's already a Date object, return it
  if (dateStr instanceof Date) {
    return dateStr;
  }
  
  // Try to parse as ISO format first
  try {
    return parseISO(dateStr);
  } catch (e) {
    // If that fails and a format is provided, try to parse with the format
    if (format) {
      try {
        return parse(dateStr, format, new Date());
      } catch (e) {
        console.error('Failed to parse date:', e);
      }
    }
  }
  
  // If all parsing fails, return current date
  return new Date();
}

/**
 * Formats a date in a specific timezone
 * @param date Date to format
 * @param timezone Timezone to format the date in (e.g., 'America/New_York')
 * @param formatStr Format string for the output
 * @returns Formatted date string in the specified timezone
 */
export function formatDateInTimezone(
  date: Date | string,
  timezone: string,
  formatStr: string = 'yyyy-MM-dd HH:mm:ss'
): string {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  
  try {
    return formatInTimeZone(dateObj, timezone, formatStr);
  } catch (error) {
    console.error('Error formatting date in timezone:', error);
    // Fallback to regular formatting without timezone
    return format(dateObj, formatStr);
  }
}

/**
 * Formats a timestamp as a relative time (e.g., "2 hours ago")
 * @param timestamp Timestamp to format
 * @param options Optional configuration for formatting
 * @returns Formatted relative time string
 */
export function formatRelativeTime(
  timestamp: Date | string | number,
  options: { addSuffix?: boolean } = { addSuffix: true }
): string {
  if (!timestamp) return '';
  
  let date: Date;
  
  // Convert the timestamp to a Date object
  if (typeof timestamp === 'string') {
    // Try to parse as ISO string
    date = parseISO(timestamp);
  } else if (typeof timestamp === 'number') {
    // Assume it's a Unix timestamp in milliseconds
    date = new Date(timestamp);
  } else {
    // Already a Date object
    date = timestamp;
  }
  
  // Check if the date is valid
  if (!isValid(date)) {
    console.error('Invalid date for relative time formatting:', timestamp);
    return '';
  }
  
  // Format the relative time
  try {
    return formatDistanceToNow(date, options);
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '';
  }
}

/**
 * Formats a time component with leading zeros
 * @param value The time component value
 * @returns Formatted time component string with leading zeros
 */
export function formatTimeComponent(value: number): string {
  return value.toString().padStart(2, '0');
}

/**
 * Converts a date string to a UTC timestamp
 * @param dateStr Date string to convert
 * @param format Format of the date string
 * @returns Date object in UTC
 */
export function toUtcTimestamp(dateStr: string, format: string): Date {
  // Parse the date string using the provided format
  const parsedDate = parse(dateStr, format, new Date());
  
  // Convert to UTC
  return new Date(
    Date.UTC(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      parsedDate.getHours(),
      parsedDate.getMinutes(),
      parsedDate.getSeconds()
    )
  );
}

/**
 * Sets time components for a date
 * @param date Date to modify
 * @param options Object with time components to set
 * @returns New date with updated time components
 */
export function setTimeForDate(
  date: Date | string, 
  options: { hours?: number; minutes?: number; seconds?: number }
): Date {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
    // Be more explicit with the set function
  return set(dateObj, {
    hours: options.hours,
    minutes: options.minutes,
    seconds: options.seconds
  });
}

/**
 * Gets the month from a date (0-11)
 * @param date Date to extract month from
 * @returns Month number (0-11)
 */
export function getMonthUtil(date: Date): number {
  return getMonth(date);
}

/**
 * Adds the specified number of months to a date
 * @param date Date to add months to
 * @param months Number of months to add
 * @returns Date with added months
 */
export function addMonthsUtil(date: Date, months: number): Date {
  return addMonths(date, months);
}

/**
 * Parses a UTC date string in format 'YYYY-MM-DD HH:mm:ss' and treats it as UTC
 * @param dateString Date string in UTC format
 * @returns Date object parsed as UTC
 */
export function parseUtcDateString(dateString: string): Date {
  // Add 'Z' to indicate UTC timezone if not present
  const utcString = dateString.includes('Z') ? dateString : dateString + 'Z';
  return parseISO(utcString.replace(' ', 'T')); // Replace space with T for ISO format
}

/**
 * Calculates duration from current time to a future date (for token validity)
 * @param futureDate Future date (token expiry)
 * @param currentDate Current date (defaults to now)
 * @returns Duration object with positive values if token is valid, negative if expired
 */
export function getTimeUntilExpiry(futureDate: Date | string, currentDate?: Date): {
  asMilliseconds: () => number;
  asSeconds: () => number;
  asMinutes: () => number;
  asHours: () => number;
  asDays: () => number;
  days: () => number;
  hours: () => number;
  minutes: () => number;
  seconds: () => number;
  isExpired: () => boolean;
  humanize: () => string;
} {
  const futureDateObj = typeof futureDate === 'string' ? parseUtcDateString(futureDate) : futureDate;
  const currentDateObj = currentDate || getCurrentDate();
  
  const diffInMilliseconds = differenceInMilliseconds(futureDateObj, currentDateObj);
  
  const totalSeconds = Math.abs(diffInMilliseconds) / 1000;
  const totalMinutes = totalSeconds / 60;
  const totalHours = totalMinutes / 60;
  const totalDays = totalHours / 24;
  
  const days = Math.floor(totalDays);
  const hours = Math.floor(totalHours % 24);
  const minutes = Math.floor(totalMinutes % 60);
  const seconds = Math.floor(totalSeconds % 60);
  
  return {
    asMilliseconds: () => diffInMilliseconds,
    asSeconds: () => diffInMilliseconds / 1000,
    asMinutes: () => diffInMilliseconds / (1000 * 60),
    asHours: () => diffInMilliseconds / (1000 * 60 * 60),
    asDays: () => diffInMilliseconds / (1000 * 60 * 60 * 24),
    days: () => days,
    hours: () => hours,
    minutes: () => minutes,
    seconds: () => seconds,
    isExpired: () => diffInMilliseconds < 0,
    humanize: () => {
      const prefix = diffInMilliseconds < 0 ? 'expired ' : '';
      if (totalDays >= 1) return `${prefix}${days} day${days !== 1 ? 's' : ''}`;
      if (totalHours >= 1) return `${prefix}${hours} hour${hours !== 1 ? 's' : ''}`;
      if (totalMinutes >= 1) return `${prefix}${minutes} minute${minutes !== 1 ? 's' : ''}`;
      return `${prefix}${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
  };
}

/**
 * Calculates the difference between two dates in milliseconds
 * @param dateLeft First date (later date for positive result)
 * @param dateRight Second date (earlier date for positive result)
 * @returns Difference in milliseconds
 */
export function differenceInMillisecondsUtil(dateLeft: Date | string, dateRight: Date | string): number {
  const dateLeftObj = typeof dateLeft === 'string' ? parseISO(dateLeft) : dateLeft;
  const dateRightObj = typeof dateRight === 'string' ? parseISO(dateRight) : dateRight;
  return differenceInMilliseconds(dateLeftObj, dateRightObj);
}


/**
 * Formats a date string according to the specified format and timezone
 * @param dateString ISO date string to format
 * @param formatString Format string to use
 * @param timezone Timezone to use (UTC or JST)
 * @returns Formatted date string
 */
export function utcToSelectedTimezone(
  dateString: string,
  formatString: string,
  timezone: string,
): string {
  // Takes dateString as UTC and gives formatted timezone dates
  if (!dateString) return "";
  try {
    const date = parseJSON(dateString);
    // date                             //date
    // parseISO(date)                   //date as local
    // parseJSON(date)                  //UTC to local
    // toZonedTime(date, "UTC"),        //local to UTC
    // formatInTimeZone(date, "UTC", "yyyy-MM-dd HH:mm:ss zzzz")  //date as UTC

    if (!isValid(date)) {
      return dateString; // Return original if parsing fails
    }

    if (timezone === "JST") {
      return toJSTDateTime(date, formatString);
    } else {
      // Assume UTC for any other timezone
      const utcDate = toZonedTime(date, "UTC");
      return formatDate(utcDate, formatString);
    }
  } catch (error) {
    console.error("Error formatting date with timezone:", error);
    return dateString; // Return original on error
  }
}

/**
 * Parses a time string in "HH:mm" format, returning a Date object or null if parsing fails.
 * @param timeStr The time string to parse.
 * @returns A Date object representing the parsed time, or null if parsing fails.
 */
export function parseTimeString(timeStr: string): Date | null {
    try {
        const parsedDate = parse(timeStr, "HH:mm", new Date());
        if (isValid(parsedDate)) {
            return parsedDate;
        } else {
            console.warn(`Invalid time string: ${timeStr}`);
            return null;
        }
    } catch (error) {
        console.error(`Error parsing time string ${timeStr}:`, error);
        return null;
    }
}

/**
 * Gets the current date and time in UTC, formatted as 'yyyy-MM-dd HH:mm:ss'.
 * @returns Current date and time in UTC, formatted as 'yyyy-MM-dd HH:mm:ss'.
 */
export function getCurrentUTCDateFormatted(): string {
  const now = new Date(); // Get current date/time in local timezone
  const utcDate = toZonedTime(now, 'UTC'); // Convert to UTC
  return format(utcDate, 'yyyy-MM-dd HH:mm:ss'); // Format as 'yyyy-MM-dd HH:mm:ss'
}